'use client';

import React, { useEffect } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Loader2, AlertCircle } from 'lucide-react';
import { CommandResult } from '@/types/slash-commands'; // Import CommandResult

interface CommandResultPopoverProps {
  commandId: string | null;
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  result: CommandResult | null; // Use CommandResult type
  targetElement: HTMLElement | null; // Element to anchor the popover to
  onClose: () => void;
}

export function CommandResultPopover({
  commandId,
  isOpen,
  isLoading,
  error,
  result,
  targetElement,
  onClose,
}: CommandResultPopoverProps) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  let content: React.ReactNode;

  if (isLoading) {
    // Customize loading text based on commandId
    let loadingText = 'Loading...'; // Default
    switch (commandId) {
      case 'nowWhat':
        loadingText = 'Finding suggestions...';
        break;
      case 'findSynonym':
        loadingText = 'Finding synonyms...';
        break;
      case 'findExample':
        loadingText = 'Finding examples...';
        break;
      case 'checkTopicSentence':
        loadingText = 'Checking topic sentence...';
        break;
      case 'checkIntroduction':
        loadingText = 'Checking introduction...';
        break;
      case 'checkConclusion':
        loadingText = 'Checking conclusion...';
        break;
    }

    content = (
      <div className='p-3 flex items-center justify-center text-sm text-muted-foreground italic'>
        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
        {loadingText}
      </div>
    );
  } else if (error) {
    content = (
      <div className='p-3 flex items-center text-sm text-destructive'>
        <AlertCircle className='h-4 w-4 mr-2 flex-shrink-0' />
        <span>{error}</span>
      </div>
    );
  } else if (result) {
    // Check for suggestions first
    if (result.suggestions && result.suggestions.length > 0) {
      const isInteractive = false; // Keep non-interactive for now
      content = (
        <div className='max-h-48 overflow-y-auto p-1'>
          {result.suggestions.map((item, index) => (
            <div
              key={index}
              // Apply interactive styles only if isInteractive is true
              className={cn(
                'px-2 py-1.5 text-sm rounded-sm',
                isInteractive && 'cursor-pointer hover:bg-accent/50'
              )}
              // Add onClick handler only if interactive
              // onClick={isInteractive ? () => console.log('Clicked:', item) : undefined}
            >
              {item}
            </div>
          ))}
        </div>
      );
    } else if (result.feedback) {
      // Display single string feedback
      content = <div className='p-3 text-sm'>{result.feedback}</div>;
    } else {
      // Handle case where result exists but has no suggestions or feedback (or is empty array)
      content = (
        <div className='p-3 text-sm text-muted-foreground'>
          No specific suggestions or feedback available.
        </div>
      );
    }
  } else {
    // Should not happen if opened correctly, but handle gracefully
    content = (
      <div className='p-3 text-sm text-muted-foreground'>
        No result available.
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {targetElement && (
        <PopoverAnchor virtualRef={{ current: targetElement }} />
      )}
      <PopoverContent
        className='w-64 md:w-96 p-0 shadow-xl' // Use w-96 consistent with NowWhat, remove padding
        sideOffset={5}
        align='start'
        onOpenAutoFocus={(e) => e.preventDefault()} // Prevent focus stealing
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}
